import { useCallback, useState } from "react"
import { useRecoilState } from "recoil"
import { useParams } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { Button, HeaderRow, Row, Col } from "../App.styles"
import { Pagination, SidebarLayout, Spinner } from "../common"
import { Alert, Refresher } from "../common"
import Datalist from "./Datalist"
import DatalistItem from "./DatalistItem"
import DatalistPlaceholder from "./DatalistPlaceholder"
import { DatalistHeader } from "./Datalist.style"
import { withPlaceholder } from "../common"
import { useCreateMutation, useDestroyMutation, useGroupsDataQuery } from '../groups/Groups.state'

/**
 * A generic, paginated list component used primarily for rendering groups or similar 
 * data entities. It handles creation, deletion, and pagination logic while wrapping 
 * the list in a `SidebarLayout`.
 * 
 * @param {Object} props
 * @param {boolean} [props.paginated=false] - Whether to show the pagination controls.
 * @param {Object} props.sidebar - Sidebar configuration containing `message` and `panel`.
 * @param {Object} props.partials - Optional render props for specific sections (e.g., `delete`).
 * @param {Object} props.states - Recoil state atoms for managing local state (notification, page, etc.).
 * @param {string} props.dataId - The key used to extract the ID from the creation response (e.g., 'group').
 * @param {React.ComponentType} [props.empty=null] - Component to render when the list is empty.
 * @returns {React.ReactElement} The rendered datalist with controls.
 */
const DatalistItems = ({ paginated = false, sidebar, partials, states, dataId, empty = null }) => {
    const { t } = useTranslation()
    const { game_id } = useParams()
    const { isCreating, create } = useCreateMutation()
    const { isDeleting, destroy } = useDestroyMutation()
    const { data, isFetching } = useGroupsDataQuery(game_id)
    const pages = data?.pages || []
    const listData = data?.groups || []
    const [{ color, message }] = useRecoilState(states.notification)
    const [, setDeleteId] = useRecoilState(states.deleteId)
    const [page, setPage] = useRecoilState(states.page)
    const [pageSize, setPageSizeState] = useRecoilState(states.pageSize)
    const [scrollId, setScrollId] = useState(0)
    const setPageSize = pageSize => {
        setPageSizeState(Number(pageSize))
    }
    const onCreate = useCallback(async () => {
        const updatePage = (currentPage) => {
            if (Number(currentPage) !== Number(page)) {
                setPage(currentPage)
            }
        }
        try {
            const { data } = await create()
            if (data?.meta?.page) updatePage(data.meta.page)
            if (data[dataId]?.id) setScrollId(data[dataId].id)
        } catch {}
    }, [create, page, setPage, dataId])

    const EmptyComponent = empty

    return (
        <>
            <Row>
                <Col size="12">
                    <Alert color={color}>{message}</Alert>
                </Col>
            </Row>
            {
                empty && listData.length === 0
                    ? null
                    : (
                        <HeaderRow>
                            <DatalistHeader className="col-12 col-xl-9">
                                <Button color="primary" onClick={onCreate} disabled={isCreating}>
                                    {t('Add New')} {isCreating && <Spinner size="0.875rem" />}
                                </Button>
                            </DatalistHeader>
                        </HeaderRow>
                    )
            }
            <Row>
                <SidebarLayout
                    message={sidebar.message}
                    panel={sidebar.panel}
                >
                    {
                        listData.length > 0
                            ? (
                                <Datalist>
                                    {
                                        listData.map((row, index) => (
                                            <DatalistItem
                                                index={index}
                                                key={row.id}
                                                {...row}
                                                deleteSection={
                                                    partials?.delete
                                                        ? partials.delete({
                                                            confirmAction: async () => {
                                                                await setDeleteId(row.id)
                                                                destroy()
                                                            },
                                                            isLoading: isDeleting,
                                                            t,
                                                        })
                                                        : null
                                                }
                                                game_id={game_id}
                                                scrollId={scrollId}
                                            />
                                        ))
                                    }
                                </Datalist>
                            )
                            : <EmptyComponent isCreating={isCreating} onCreate={onCreate} />
                    }
                </SidebarLayout>
            </Row>
            {
                paginated
                    ? (
                        <Row>
                            <Pagination
                                loading={isFetching}
                                page={page}
                                pageSize={pageSize}
                                pages={pages}
                                setPage={setPage}
                                setPageSize={setPageSize}
                            />
                        </Row>
                    )
                    : null
            }
            <Refresher />
        </>
    )
}

export default withPlaceholder(DatalistItems, DatalistPlaceholder)
